import { useMemo, useState } from 'react';
import { A2UICockpitSurface } from './A2UICockpitSurface';
import { buildWorldSemanticObservation, proposePromoteShare, routeCockpitInput, type CockpitContext, type CockpitInput, type CockpitModality, type CockpitPrivacy, type CockpitTransport } from './cockpit-consumer';

type Option<T extends string> = { value: T; label: string };

const contexts: Option<CockpitContext>[] = [
  { value: 'personal', label: 'Personal' },
  { value: 'work', label: 'Work' },
  { value: 'world', label: 'World' },
  { value: 'build', label: 'Build' },
];

const transports: Option<CockpitTransport>[] = [
  { value: 'app', label: 'App' },
  { value: 'voice', label: 'Voice' },
  { value: 'phone', label: 'Phone' },
  { value: 'telegram', label: 'Telegram' },
  { value: 'web', label: 'Web' },
];

const modalities: Option<CockpitModality>[] = [
  { value: 'text', label: 'Text' },
  { value: 'voice', label: 'Voice' },
  { value: 'image', label: 'Image' },
  { value: 'video', label: 'Video' },
  { value: 'pdf', label: 'PDF' },
  { value: 'link', label: 'Link' },
  { value: 'sensor', label: 'Sensor' },
];

const privacyOptions: Option<CockpitPrivacy>[] = [
  { value: 'private', label: 'Private' },
  { value: 'shared', label: 'Shared' },
  { value: 'public', label: 'Public' },
];

function Segment<T extends string>({ label, value, options, onChange }: { label: string; value: T; options: Option<T>[]; onChange: (value: T) => void }) {
  return <label className="cockpit-segment"><span>{label}</span><div>{options.map((option) => <button key={option.value} type="button" data-active={option.value === value} onClick={() => onChange(option.value)}>{option.label}</button>)}</div></label>;
}

export function CockpitSurface() {
  const [context, setContext] = useState<CockpitContext>('world');
  const [transport, setTransport] = useState<CockpitTransport>('telegram');
  const [modality, setModality] = useState<CockpitModality>('text');
  const [privacy, setPrivacy] = useState<CockpitPrivacy>('private');
  const [contentRef, setContentRef] = useState('olav:note:family-ritual');
  const [purpose, setPurpose] = useState('world_semantic_replay');

  const input: CockpitInput = useMemo(() => ({
    id: 'cockpit-preview-1',
    transport,
    context,
    modality,
    contentRef,
    owner: 'human-owner',
    purpose,
    privacy,
    sourceRef: `${transport}:preview`,
    evidenceRefs: [`${transport}:preview`],
    createdAt: '2026-08-10T03:57:21.000Z',
  }), [contentRef, context, modality, privacy, purpose, transport]);

  const envelope = routeCockpitInput(input);
  const promote = proposePromoteShare(envelope);
  const worldObservation = context === 'world' ? buildWorldSemanticObservation(input) : undefined;

  return <section className="surface-card page-card cockpit-page">
    <div className="section-header">
      <div>
        <p className="eyebrow">UNIFIED COCKPIT</p>
        <h2>One front door. Four contexts. One execution gate.</h2>
        <p>Text, voice, phone, Telegram, app and web produce the same governed input envelope. The cockpit routes. It does not authorize.</p>
      </div>
    </div>

    <div className="cockpit-flow" aria-label="Cockpit routing map">
      <article data-active="true"><span>Input</span><strong>{transport}</strong><small>{modality}</small></article>
      <i>→</i>
      <article data-active="true"><span>Context</span><strong>{context}</strong><small>{privacy}</small></article>
      <i>→</i>
      <article><span>Adapter</span><strong>{envelope.route.adapter}</strong><small>{envelope.route.target}</small></article>
      <i>→</i>
      <article><span>Gate</span><strong>{envelope.authorizationBoundary}</strong><small>{envelope.executionAuthority}</small></article>
    </div>

    <div className="cockpit-grid">
      <div className="cockpit-panel">
        <Segment label="Context" value={context} options={contexts} onChange={setContext} />
        <Segment label="Transport" value={transport} options={transports} onChange={setTransport} />
        <Segment label="Modality" value={modality} options={modalities} onChange={setModality} />
        <Segment label="Privacy" value={privacy} options={privacyOptions} onChange={setPrivacy} />
        <label className="cockpit-field"><span>Content reference</span><input value={contentRef} onChange={(event) => setContentRef(event.target.value)} /></label>
        <label className="cockpit-field"><span>Purpose</span><input value={purpose} onChange={(event) => setPurpose(event.target.value)} /></label>
      </div>

      <div className="cockpit-panel cockpit-output">
        <div><span>Route</span><strong>{envelope.route.target}</strong><small>{envelope.route.eventKind}</small></div>
        <div><span>Canonical event</span><strong>{envelope.canonicalEvent.eventId}</strong><small>{envelope.canonicalEvent.idempotencyKey}</small></div>
        <div><span>Boundary</span><strong>{envelope.authorizationBoundary}</strong><small>Execution authority: {envelope.executionAuthority}</small></div>
        <div><span>Promote/share</span><strong>{envelope.promoteRequired ? 'Required' : 'Not required'}</strong><small>{promote ? promote.purpose : 'Private personal input stays local'}</small></div>
      </div>
    </div>

    {worldObservation && <div className="cockpit-observation">
      <p className="eyebrow">OLAV → KERNEL</p>
      <h3>World input becomes an OLAV semantic observation.</h3>
      <code>{JSON.stringify(worldObservation, null, 2)}</code>
    </div>}

    <div className="cockpit-observation">
      <p className="eyebrow">AGENT-GENERATED UI</p>
      <h3>A2UI controls presentation. VALO controls authority.</h3>
      <A2UICockpitSurface />
    </div>

    <div className="principle-banner"><strong>No fourth OS.</strong><span>The cockpit is a routing and approval surface in front of Personal OS, Work OS, World OS and Software Factory. REHT remains the only execution authorization gate.</span></div>
  </section>;
}
